import { apiClient } from '@/lib/apiClient';
import type {
  Event,
  CreateEventRequest,
  UpdateEventRequest,
  Task,
  CreateTaskRequest,
  UpdateTaskRequest,
  Reminder,
  CreateReminderRequest,
  NotificationPreferences,
  UpdateNotificationPreferencesRequest,
  EventConflict,
  BulkEventRequest,
  BulkEventResponse,
  EventRecurrence,
  CreateRecurrenceRequest,
  EventQueryParams,
  TaskQueryParams
} from '@/types/scheduler';

class SchedulerApiService {
  // Events API
  async listEvents(params?: EventQueryParams & { user_id?: number }): Promise<Event[]> {
    const queryParams = new URLSearchParams();
    let url = '/scheduler/events';
    
    if (params) {
      const { user_id, ...otherParams } = params;
      
      // If user_id is provided, use the new path parameter format
      if (user_id) {
        url = `/scheduler/events/user/${user_id}`;
      }
      
      // Add remaining parameters as query parameters
      Object.entries(otherParams).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const finalUrl = `${url}${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{events: Event[]}>(finalUrl);
    return response.events;
  }

  async createEvent(eventData: CreateEventRequest): Promise<Event> {
    return await apiClient.post<Event>('/scheduler/events', eventData);
  }

  async getEvent(eventId: string): Promise<Event> {
    return await apiClient.get<Event>(`/scheduler/events/${eventId}`);
  }

  async updateEvent(eventId: string, eventData: UpdateEventRequest): Promise<Event> {
    return await apiClient.put<Event>(`/scheduler/events/${eventId}`, eventData);
  }

  async deleteEvent(eventId: string): Promise<void> {
    await apiClient.delete(`/scheduler/events/${eventId}`);
  }

  async rescheduleEvent(eventId: string, newStartDate: string, newEndDate: string): Promise<Event> {
    return await apiClient.post<Event>(`/scheduler/events/${eventId}/reschedule`, {
      new_start_date: newStartDate,
      new_end_date: newEndDate
    });
  }

  async cancelEvent(eventId: string, reason?: string): Promise<Event> {
    return await apiClient.post<Event>(`/scheduler/events/${eventId}/cancel`, {
      reason
    });
  }

  async checkEventConflicts(startDate: string, endDate: string, excludeEventId?: string): Promise<EventConflict[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    if (excludeEventId) {
      params.append('exclude_event_id', excludeEventId);
    }
    
    return await apiClient.get<EventConflict[]>(`/scheduler/events/conflicts?${params}`);
  }

  async bulkCreateEvents(eventsData: BulkEventRequest): Promise<BulkEventResponse> {
    return await apiClient.post<BulkEventResponse>('/scheduler/events/bulk', eventsData);
  }

  async completeEvent(eventId: string): Promise<Event> {
    return await apiClient.post<Event>(`/scheduler/events/${eventId}/complete`);
  }

  async incompleteEvent(eventId: string): Promise<Event> {
    return await apiClient.post<Event>(`/scheduler/events/${eventId}/incomplete`);
  }

  // Tasks API
  async listTasks(params?: TaskQueryParams & { user_id?: number }): Promise<Task[]> {
    const queryParams = new URLSearchParams();
    let url = '/scheduler/tasks';
    
    if (params) {
      const { user_id, ...otherParams } = params;
      
      // If user_id is provided, use the new path parameter format
      if (user_id) {
        url = `/scheduler/tasks/user/${user_id}`;
      }
      
      // Add remaining parameters as query parameters
      Object.entries(otherParams).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const finalUrl = `${url}${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{tasks: Task[]}>(finalUrl);
    return response.tasks || [];
  }

  async createTask(taskData: CreateTaskRequest): Promise<Task> {
    return await apiClient.post<Task>('/scheduler/tasks', taskData);
  }

  async getTask(taskId: string): Promise<Task> {
    return await apiClient.get<Task>(`/scheduler/tasks/${taskId}`);
  }

  async updateTask(taskId: string, taskData: UpdateTaskRequest): Promise<Task> {
    return await apiClient.put<Task>(`/scheduler/tasks/${taskId}`, taskData);
  }

  async deleteTask(taskId: string): Promise<void> {
    await apiClient.delete(`/scheduler/tasks/${taskId}`);
  }

  async getTaskDependents(taskId: string): Promise<Task[]> {
    return await apiClient.get<Task[]>(`/scheduler/tasks/${taskId}/dependents`);
  }

  // Reminders API
  async listReminders(): Promise<Reminder[]> {
    return await apiClient.get<Reminder[]>('/scheduler/schedule_reminders');
  }

  async createReminder(reminderData: CreateReminderRequest): Promise<Reminder> {
    return await apiClient.post<Reminder>('/scheduler/schedule_reminders', reminderData);
  }

  async getReminder(reminderId: string): Promise<Reminder> {
    return await apiClient.get<Reminder>(`/scheduler/schedule_reminders/${reminderId}`);
  }

  async updateReminder(reminderId: string, reminderData: Partial<CreateReminderRequest>): Promise<Reminder> {
    return await apiClient.put<Reminder>(`/scheduler/schedule_reminders/${reminderId}`, reminderData);
  }

  async deleteReminder(reminderId: string): Promise<void> {
    await apiClient.delete(`/scheduler/schedule_reminders/${reminderId}`);
  }

  async snoozeReminder(reminderId: string, snoozeMinutes: number): Promise<Reminder> {
    return await apiClient.post<Reminder>(`/scheduler/schedule_reminders/${reminderId}/snooze`, {
      snooze_minutes: snoozeMinutes
    });
  }

  async dismissReminder(reminderId: string): Promise<void> {
    await apiClient.post(`/scheduler/schedule_reminders/${reminderId}/dismiss`);
  }

  async triggerReminder(reminderId: string): Promise<void> {
    await apiClient.post(`/scheduler/schedule_reminders/${reminderId}/trigger`);
  }

  async getPendingReminders(): Promise<Reminder[]> {
    return await apiClient.get<Reminder[]>('/scheduler/schedule_reminders/pending');
  }

  // Notification Preferences API
  async getNotificationPreferences(userId: number): Promise<NotificationPreferences> {
    return await apiClient.get<NotificationPreferences>(`/scheduler/notification_preferences/${userId}`);
  }

  async updateNotificationPreferences(userId: number, preferences: UpdateNotificationPreferencesRequest): Promise<NotificationPreferences> {
    return await apiClient.put<NotificationPreferences>(`/scheduler/notification_preferences/${userId}`, preferences);
  }

  // Event Recurrence API
  async createEventRecurrence(eventId: string, recurrenceData: CreateRecurrenceRequest): Promise<EventRecurrence> {
    return await apiClient.post<EventRecurrence>(`/scheduler/events/${eventId}/recurrence`, recurrenceData);
  }

  async updateEventRecurrence(eventId: string, recurrenceData: Partial<CreateRecurrenceRequest>): Promise<EventRecurrence> {
    return await apiClient.put<EventRecurrence>(`/scheduler/events/${eventId}/recurrence`, recurrenceData);
  }
}

export const schedulerApi = new SchedulerApiService();